// src/components/controlRondas/NfcTagsManager.jsx
import React, { useState, useEffect, useContext } from 'react';
import { collection, getDocs, doc, updateDoc, addDoc, deleteDoc, query, where } from 'firebase/firestore';
import { db } from '../../../firebase';
import { Smartphone, Plus, Edit, Trash, Search, AlertCircle, Info, HelpCircle } from 'lucide-react';
import { AuthContext } from "../../../context/AuthContext";
import NFCReader from '../NFCReader';
import NfcTutorial from './NfcTutorial';
import '../styles/NfcTagsManager.scss';

const NfcTagsManager = () => {
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [editingTagId, setEditingTagId] = useState(null);
    const [showNFCReader, setShowNFCReader] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredTags, setFilteredTags] = useState([]);
    const [showTutorial, setShowTutorial] = useState(false);

    const [deviceInfo, setDeviceInfo] = useState({
        isMobile: false,
        supportsNfc: false
    });

    const { currentService } = useContext(AuthContext);

    // Nuevo tag
    const [newTag, setNewTag] = useState({
        tagId: '',   // ID físico de la etiqueta NFC
        name: '',    // Nombre asignado por el usuario
        location: '', // Ubicación descriptiva 
        checkpointId: null, // ID del punto asociado (si existe)
        lastRead: null,
        createdAt: null
    });

    useEffect(() => {
        // Detectar si es dispositivo móvil
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        // Detectar si soporta NFC
        const supportsNfc = 'NDEFReader' in window;

        setDeviceInfo({
            isMobile,
            supportsNfc
        });

        if (currentService) {
            fetchTags();
        }
    }, [currentService]);

    useEffect(() => {
        if (tags.length > 0) {
            if (searchTerm) {
                const filtered = tags.filter(tag =>
                    tag.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    tag.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    tag.tagId?.toLowerCase().includes(searchTerm.toLowerCase())
                );
                setFilteredTags(filtered);
            } else {
                setFilteredTags(tags);
            }
        } else {
            setFilteredTags([]);
        }
    }, [tags, searchTerm]);

    const fetchTags = async () => {
        try {
            setLoading(true);
            // Obtener las etiquetas NFC de la subcolección del servicio actual
            const tagsRef = collection(db, 'servicios', currentService.id, 'nfcTags');
            const querySnapshot = await getDocs(tagsRef);

            const tagsList = querySnapshot.docs.map(doc => ({
                id: doc.id,  // ID del documento
                ...doc.data()
            }));

            setTags(tagsList);
            setFilteredTags(tagsList);
            setLoading(false);
        } catch (err) {
            console.error('Error al cargar etiquetas NFC:', err);
            setError('Error al cargar las etiquetas NFC. Por favor, inténtalo de nuevo.');
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewTag(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const resetForm = () => {
        setNewTag({
            tagId: '',
            name: '',
            location: '',
            checkpointId: null,
            lastRead: null
        });
        setIsAddingNew(false);
        setEditingTagId(null);
        setShowNFCReader(false);
    };

    const handleNFCTagRead = (tagData) => {
        setNewTag(prev => ({
            ...prev,
            tagId: tagData.id,
            nfcData: tagData.data || {}
        }));
        setShowNFCReader(false);
    };

    const handleAddTag = async () => {
        if (!newTag.tagId) {
            setError('Debes escanear una etiqueta NFC');
            return;
        }

        if (!newTag.name) {
            setError('El nombre de la etiqueta es obligatorio');
            return;
        }

        try {
            setLoading(true);

            // Verificar si ya existe una etiqueta con este ID físico
            const tagsRef = collection(db, 'servicios', currentService.id, 'nfcTags');
            const q = query(tagsRef, where('tagId', '==', newTag.tagId));
            const querySnapshot = await getDocs(q);

            // Verificar duplicados, excluyendo la etiqueta que estamos editando
            const exists = querySnapshot.docs.some(doc =>
                doc.id !== editingTagId
            );

            if (exists) {
                setError('Ya existe una etiqueta con este ID NFC. Utiliza otra etiqueta.');
                setLoading(false);
                return;
            }

            if (editingTagId) {
                // Actualizar etiqueta existente
                await updateDoc(doc(db, 'servicios', currentService.id, 'nfcTags', editingTagId), {
                    name: newTag.name,
                    location: newTag.location,
                    tagId: newTag.tagId,
                    nfcData: newTag.nfcData || {},
                    updatedAt: new Date()
                });
            } else {
                // Crear nueva etiqueta
                await addDoc(collection(db, 'servicios', currentService.id, 'nfcTags'), {
                    ...newTag,
                    createdAt: new Date(),
                    updatedAt: new Date()
                });
            }

            resetForm();
            fetchTags();
        } catch (err) {
            console.error('Error al guardar etiqueta NFC:', err);
            setError('Error al guardar la etiqueta NFC. Por favor, inténtalo de nuevo.');
            setLoading(false);
        }
    };

    const handleEditTag = (tag) => {
        setNewTag({
            tagId: tag.tagId || '',
            name: tag.name || '',
            location: tag.location || '',
            nfcData: tag.nfcData || {},
            checkpointId: tag.checkpointId || null,
            lastRead: tag.lastRead || null
        });
        setEditingTagId(tag.id);
        setIsAddingNew(true);
    };

    const handleDeleteTag = async (tagId) => {
        if (window.confirm('¿Estás seguro de eliminar esta etiqueta NFC?')) {
            try {
                await deleteDoc(doc(db, 'servicios', currentService.id, 'nfcTags', tagId));
                fetchTags();
            } catch (err) {
                console.error('Error al eliminar etiqueta NFC:', err);
                setError('Error al eliminar la etiqueta NFC. Por favor, inténtalo de nuevo.');
            }
        }
    };

    return (
        <div className="nfc-tags-manager">
            <div className="manager-header">
                <h2>Etiquetas NFC</h2>
                <div className="header-actions">
                    <button
                        className="btn-help"
                        onClick={() => setShowTutorial(true)}
                        title="Ver tutorial"
                    >
                        <HelpCircle size={16} />
                    </button>
                    <div className="search-container">
                        <Search size={16} className="search-icon" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Buscar etiquetas..."
                            className="search-input"
                        />
                    </div>
                    {!isAddingNew && (
                        <button
                            onClick={() => setIsAddingNew(true)}
                            className="btn-add"
                        >
                            <Plus size={16} className="btn-icon" />
                            Nueva Etiqueta
                        </button>
                    )}
                </div>
            </div>

            {/* Aviso de compatibilidad NFC */}
            {!deviceInfo.supportsNfc && (
                <div className="nfc-compatibility-alert">
                    <Info size={20} className="info-icon" />
                    <div className="compatibility-message">
                        <strong>Compatibilidad NFC:</strong> Para registrar etiquetas NFC, necesitas un dispositivo Android con Chrome/Edge.
                        La lectura NFC no funciona en iOS ni en navegadores de escritorio.
                    </div>
                </div>
            )}

            {error && (
                <div className="alert alert-error">
                    <AlertCircle size={18} className="alert-icon" />
                    <span>{error}</span>
                    <button
                        onClick={() => setError(null)}
                        className="alert-close"
                    >
                        ×
                    </button>
                </div>
            )}

            {isAddingNew && (
                <div className="tag-form">
                    <div className="form-header">
                        <h3>{editingTagId ? 'Editar Etiqueta' : 'Nueva Etiqueta NFC'}</h3>
                        <button
                            onClick={resetForm}
                            className="btn-close"
                        >
                            ×
                        </button>
                    </div>

                    <div className="form-content">
                        <div className="form-group">
                            <label className="form-label">ID de Etiqueta NFC</label>
                            <div className="nfc-field">
                                <input
                                    type="text"
                                    value={newTag.tagId}
                                    readOnly
                                    className="form-input nfc-input"
                                    placeholder="Escanea una etiqueta NFC"
                                />
                                <button
                                    onClick={() => setShowNFCReader(true)}
                                    className="btn-scan"
                                    disabled={!deviceInfo.supportsNfc}
                                >
                                    <Smartphone size={16} className="btn-icon" />
                                    Escanear
                                </button>
                            </div>
                            {!deviceInfo.supportsNfc && (
                                <p className="form-help error">
                                    Tu dispositivo o navegador no soporta NFC. Usa un dispositivo Android con Chrome/Edge.
                                </p>
                            )}
                            {deviceInfo.supportsNfc && (
                                <p className="form-help">
                                    Este es el identificador único físico de la etiqueta NFC.
                                </p>
                            )}
                        </div>

                        <div className="form-group">
                            <label className="form-label">Nombre de la Etiqueta *</label>
                            <input
                                type="text"
                                name="name"
                                value={newTag.name}
                                onChange={handleInputChange}
                                className="form-input"
                                placeholder="Ej: Entrada Principal"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">Ubicación</label>
                            <input
                                type="text"
                                name="location"
                                value={newTag.location}
                                onChange={handleInputChange}
                                className="form-input"
                                placeholder="Ej: Puerta principal, planta 1"
                            />
                        </div>
                    </div>

                    <div className="form-actions">
                        <button
                            onClick={resetForm}
                            className="btn-cancel"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleAddTag}
                            className="btn-save"
                            disabled={!newTag.tagId || !newTag.name}
                        >
                            {editingTagId ? 'Actualizar Etiqueta' : 'Guardar Etiqueta'}
                        </button>
                    </div>
                </div>
            )}

            {loading && !isAddingNew ? (
                <div className="loading-message">Cargando etiquetas NFC...</div>
            ) : filteredTags.length === 0 && !isAddingNew ? (
                <div className="empty-state">
                    <Smartphone size={48} className="empty-icon" />
                    <p className="empty-text">
                        {searchTerm
                            ? 'No se encontraron etiquetas que coincidan con tu búsqueda'
                            : 'No hay etiquetas NFC registradas'}
                    </p>
                    <p className="empty-subtext">
                        {searchTerm
                            ? 'Intenta con otros términos o añade nuevas etiquetas'
                            : 'Las etiquetas NFC son útiles para verificar puntos de control con precisión'}
                    </p>
                    <button
                        onClick={() => setIsAddingNew(true)}
                        className="btn-add-empty"
                    >
                        <Plus size={16} className="btn-icon" />
                        Añadir Primera Etiqueta
                    </button>
                </div>
            ) : (
                <div className="nfc-tags-list">
                    {filteredTags.map((tag) => (
                        <div key={tag.id} className="tag-item">
                            <div className="tag-content">
                                <div className="tag-header">
                                    <h4 className="tag-name">{tag.name}</h4>
                                    <div className="tag-actions">
                                        <button
                                            onClick={() => handleEditTag(tag)}
                                            className="btn-edit"
                                            title="Editar"
                                        >
                                            <Edit size={16} />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteTag(tag.id)}
                                            className="btn-delete"
                                            title="Eliminar"
                                        >
                                            <Trash size={16} />
                                        </button>
                                    </div>
                                </div>

                                <div className="tag-details">
                                    <div className="tag-id">
                                        <Smartphone size={14} className="tag-icon" />
                                        <span className="label">ID:</span>
                                        <span className="value">{tag.tagId}</span>
                                    </div>

                                    {tag.location && (
                                        <div className="tag-location">
                                            <span className="label">Ubicación:</span>
                                            <span className="value">{tag.location}</span>
                                        </div>
                                    )}

                                    {tag.checkpointId && (
                                        <div className="tag-checkpoint">
                                            <span className="label">Punto asignado:</span>
                                            <span className="value">{tag.checkpointName || tag.checkpointId}</span>
                                        </div>
                                    )}

                                    {tag.lastRead && (
                                        <div className="tag-last-read">
                                            <span className="label">Última lectura:</span>
                                            <span className="value">{new Date(tag.lastRead).toLocaleString()}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showNFCReader && (
                <NFCReader
                    onTagRead={handleNFCTagRead}
                    onCancel={() => setShowNFCReader(false)}
                />
            )}
            {showTutorial && (
                <NfcTutorial onClose={() => setShowTutorial(false)} />
            )}
        </div>
    );
};

export default NfcTagsManager;